import { z } from 'zod'
import type { SpotType } from '~/lib/graphql/graphql'
import {
  GenreCode,
  LargeAreaCode,
  MediumAreaCode,
  MinimumAreaCode,
  SmallAreaCode,
} from '~/lib/graphql/scalars'
import type { AccommodationId, SpotId } from '~/lib/graphql/scalars'

const MinimumArea = z.object({
  name: z.string(),
  code: MinimumAreaCode,
  small_area_code: SmallAreaCode,
  medium_area_code: MediumAreaCode,
  large_area_code: LargeAreaCode,
  coordinate: z
    .object({
      latitude: z.number().positive(),
      longitude: z.number().positive(),
    })
    .readonly(),
  radius: z.number().int().positive(),
  display_order: z.number().int().positive(),
})

const SmallArea = z.object({
  name: z.string(),
  code: SmallAreaCode,
  medium_area_code: MediumAreaCode,
  large_area_code: LargeAreaCode,
  minimums: z.array(MinimumArea),
  display_order: z.number().int().positive(),
})

export const MediumArea = z.object({
  name: z.string(),
  code: MediumAreaCode,
  large_area_code: LargeAreaCode,
  smalls: z.array(SmallArea),
  display_order: z.number().int().positive(),
})

const LargeArea = z.object({
  name: z.string(),
  code: LargeAreaCode,
  mediums: z.array(MediumArea),
  display_order: z.number().int().positive(),
})

const Area = z.union([MinimumArea, SmallArea, MediumArea, LargeArea])

export const LargeAreas = z.array(LargeArea).readonly()

export type MinimumArea = z.infer<typeof MinimumArea>
export type SmallArea = z.infer<typeof SmallArea>
export type MediumArea = z.infer<typeof MediumArea>
export type LargeArea = z.infer<typeof LargeArea>
export type Area = z.infer<typeof Area>
export type LargeAreas = z.infer<typeof LargeAreas>

export function isLargeArea(area: Area): area is LargeArea {
  return 'mediums' in area
}

export function isMediumArea(area: Area): area is MediumArea {
  return 'smalls' in area
}

export function isSmallArea(area: Area): area is SmallArea {
  return 'minimums' in area
}

export function isMinimumArea(area: Area): area is MinimumArea {
  return 'small_area_code' in area
}

export function findMedium(
  largeAreas: LargeAreas,
  largeAreaCode: LargeAreaCode,
  mediumAreaCode: MediumAreaCode
): MediumArea {
  const largeArea = largeAreas.find((area) => area.code === largeAreaCode)
  if (!largeArea) {
    throw new Error(`LargeArea not found: ${largeAreaCode}`)
  }
  const mediumArea = largeArea.mediums.find((area) => area.code === mediumAreaCode)
  if (!mediumArea) {
    throw new Error(`MediumArea not found: ${mediumAreaCode}`)
  }
  return mediumArea
}

export function findSmall(
  medium: MediumArea,
  code: SmallAreaCode | undefined
): SmallArea | undefined {
  if (code === undefined) {
    return undefined
  }
  return medium.smalls.find((small) => small.code === code)
}

// 極小エリアコードは unique, 小エリアを絞り込む必要はない
export function findMinimum(
  medium: MediumArea,
  code: MinimumAreaCode | undefined
): MinimumArea | undefined {
  if (code === undefined) {
    return undefined
  }
  for (const small of medium.smalls) {
    for (const minimum of small.minimums) {
      if (minimum.code === code) {
        return minimum
      }
    }
  }
  return undefined
}

export type City = {
  id: string
  large_area_code: string
  medium_area_code: string
  min_area_code: string | null
  name: string
  name_kana: string
  prefecture_id: string
  small_area_code: string | null
}

export type Town = {
  city_id: string
  id: string
  large_area_code: string
  medium_area_code: string
  min_area_code: string | null
  name: string
  name_kana: string
  prefecture_id: string
  small_area_code: string | null
}

const MinorGenre = z
  .object({
    code: GenreCode,
    major_code: GenreCode,
    name: z.string(),
    tag_id: z.number().int().positive().nullable(),
    display_order: z.number().int().positive(),
  })
  .readonly()

const MajorGenre = z
  .object({
    code: GenreCode,
    name: z.string(),
    tag_id: z.number().int().positive().nullable(),
    minors: z.array(MinorGenre).readonly(),
    display_order: z.number().int().positive(),
  })
  .readonly()

const Genre = MajorGenre.or(MinorGenre)
export type Genre = z.infer<typeof Genre>

export function isMajorGenre(genre: Genre): genre is MajorGenre {
  return 'minors' in genre
}

export function isMinorGenre(genre: Genre): genre is MinorGenre {
  return 'major_code' in genre
}

export const Genres = z.array(MajorGenre).readonly()

export type Genres = z.infer<typeof Genres>
export type MajorGenre = z.infer<typeof MajorGenre>
export type MinorGenre = z.infer<typeof MinorGenre>

export type SpotMap = {
  [key: string]: Spot
}

export type Spot = {
  id: SpotId
  name: string
  type: SpotType
  accommodationId?: AccommodationId
  radius: number
  area: MinimumArea
}

export type AllMinAreasMap = {
  [key: string]: {
    smallAreaCode: string
    minAreaCode: string
    latitude: number
    longitude: number
    name: string
  }[]
}

export type TagMap = {
  [key: string]: Tag
}

export type Tag = {
  name: string
  code: string
  description?: string
  imageUrl?: string
}

export type FacetHitCounts = {
  total: number
  [code: string]: number
}
